import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckSquare, faSquare, faRobot, faTimes, faCheck, faSpinner,
    faCheckCircle, faExclamationTriangle, faUserPlus, faPlusCircle, faExchangeAlt
} from '@fortawesome/free-solid-svg-icons';

const InvoiceExtractionResults = ({
    extractedData,
    selectedFields,
    onFieldToggle,
    selectedLineItems,
    onLineItemToggle,
    onToggleAllLineItems,
    onApply,
    onDiscard,
    onReAnalyze,
    reAnalysisGuidance,
    setReAnalysisGuidance,
    isReAnalyzing,
    crmMatch = null,
    onAddPartner,
    unrecognizedUnits = [],
    availableUnits = [],
    onAddUnitToSystem,
    onSelectExistingUnit,
    hasDefaultedUnits = false,
    onChangeDefaultedUnit
}) => {
    const currencyLabels = {
        'USD': '$ USD', 'EUR': 'EUR', 'GBP': 'GBP', 'JPY': 'JPY',
        'CHF': 'CHF', 'CAD': 'CAD', 'AUD': 'AUD', 'CNY': 'CNY',
        'ZAR': 'ZAR', 'SEK': 'SEK', 'NOK': 'NOK', 'DKK': 'DKK',
        'CZK': 'CZK', 'SGD': 'SGD', 'AED': 'AED', 'INR': 'INR',
    };

    const formatFieldValue = (field, value) => {
        if (!value) return value;
        if (field === 'currency') return currencyLabels[value] || value;
        if (['net_amount', 'vat_amount', 'gross_amount'].includes(field)) {
            const num = parseFloat(value);
            return isNaN(num) ? value : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        }
        return value;
    };

    const headerFields = [
        { key: 'supplier_name', label: 'Supplier Name' },
        { key: 'supplier_address', label: 'Supplier Address' },
        { key: 'supplier_vat_number', label: 'Supplier VAT Number' },
        { key: 'supplier_iban', label: 'Supplier IBAN' },
        { key: 'buyer_entity_name', label: 'Billed To' },
        { key: 'document_language', label: 'Invoice Language' },
        { key: 'supplier_invoice_number', label: 'Supplier Invoice Number' },
        { key: 'invoice_date', label: 'Invoice Date' },
        { key: 'due_date', label: 'Due Date' },
        { key: 'currency', label: 'Currency' },
        { key: 'net_amount', label: 'Net Amount' },
        { key: 'vat_amount', label: 'VAT Amount' },
        { key: 'gross_amount', label: 'Gross Amount' },
        { key: 'payment_terms_description', label: 'Payment Terms' },
        { key: 'supply_reference', label: 'References (PO/Contract/Delivery)' },
        { key: 'notes', label: 'Notes' },
    ];

    const lineItems = extractedData?.line_items || [];

    const FieldCard = ({ field, value, selected, onToggle }) => {
        const displayValue = formatFieldValue(field.key, value);
        const hasValue = value !== null && value !== undefined && value !== '';

        return (
            <div
                className={`extraction-field-card ${selected ? 'selected' : ''} ${hasValue ? 'has-value' : 'no-value'} ${!hasValue ? 'disabled' : ''}`}
                onClick={hasValue ? onToggle : undefined}
                style={{ cursor: hasValue ? 'pointer' : 'not-allowed', opacity: hasValue ? 1 : 0.6 }}
            >
                <div className="field-checkbox">
                    <FontAwesomeIcon
                        icon={selected && hasValue ? faCheckSquare : faSquare}
                        className={selected && hasValue ? 'text-blue-600' : 'text-gray-400'}
                    />
                </div>
                <div className="field-content">
                    <div className="field-label">{field.label}</div>
                    <div className="field-value">
                        {displayValue ? (
                            <span className="value-text">{displayValue}</span>
                        ) : (
                            <span className="no-value-text">Not found</span>
                        )}
                        {field.key === 'notes' && hasValue && extractedData?.notes_en && (
                            <span className="value-text value-translation" style={{ display: 'block', color: '#64748b', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                EN: {extractedData.notes_en}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const UnrecognizedUnitRow = ({ unitCode }) => {
        const [showDropdown, setShowDropdown] = useState(false);
        const [adding, setAdding] = useState(false);

        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.5rem 0.75rem', backgroundColor: '#fff',
                borderBottom: '1px solid #fde68a', gap: '0.75rem', flexWrap: 'wrap'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#92400e' }}>
                    <code style={{
                        padding: '0.15rem 0.4rem', backgroundColor: '#fef3c7',
                        borderRadius: '0.25rem', fontWeight: 600, fontSize: '0.8rem'
                    }}>{unitCode}</code>
                    <span>is not in your system</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {!showDropdown ? (
                        <>
                            <button
                                type="button"
                                onClick={async () => {
                                    setAdding(true);
                                    try {
                                        await onAddUnitToSystem(unitCode);
                                    } catch { /* error handled by parent */ }
                                    setAdding(false);
                                }}
                                disabled={adding}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                                    padding: '0.3rem 0.6rem', backgroundColor: '#059669', color: 'white',
                                    border: 'none', borderRadius: '0.25rem', fontSize: '0.78rem',
                                    fontWeight: 600, cursor: adding ? 'wait' : 'pointer', whiteSpace: 'nowrap'
                                }}
                            >
                                {adding ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlusCircle} />}
                                Add to System
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowDropdown(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                                    padding: '0.3rem 0.6rem', backgroundColor: '#3b82f6', color: 'white',
                                    border: 'none', borderRadius: '0.25rem', fontSize: '0.78rem',
                                    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                                }}
                            >
                                <FontAwesomeIcon icon={faExchangeAlt} /> Select Existing
                            </button>
                        </>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <select
                                onChange={(e) => {
                                    if (e.target.value) {
                                        onSelectExistingUnit(unitCode, e.target.value);
                                        setShowDropdown(false);
                                    }
                                }}
                                defaultValue=""
                                style={{
                                    padding: '0.3rem 0.5rem', fontSize: '0.8rem',
                                    border: '1px solid #d1d5db', borderRadius: '0.25rem',
                                    backgroundColor: 'white', minWidth: '160px'
                                }}
                            >
                                <option value="" disabled>Select a unit...</option>
                                {availableUnits.map(u => (
                                    <option key={u.id || u.code} value={u.code}>{u.code} - {u.name}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => setShowDropdown(false)}
                                style={{
                                    padding: '0.3rem 0.5rem', backgroundColor: '#e5e7eb',
                                    border: 'none', borderRadius: '0.25rem', fontSize: '0.78rem',
                                    cursor: 'pointer', color: '#6b7280'
                                }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const hasSelectedFields = Object.values(selectedFields).some(Boolean);

    return (
        <div className="modal-body">
            <div className="extraction-header">
                <div className="extraction-success-icon">
                    <FontAwesomeIcon icon={faRobot} style={{ color: '#3b82f6', fontSize: '1.5rem' }} />
                </div>
                <div className="extraction-message">
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1.25rem', fontWeight: 600 }}>
                        Invoice Extraction Complete
                    </h3>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                        Review the extracted information and select which fields to use for creating your invoice.
                    </p>
                </div>
            </div>

            {/* CRM Supplier Match Status */}
            {extractedData?.supplier_name && (
                <div style={{ marginBottom: '1rem' }}>
                    {crmMatch === null ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.6rem 1rem', backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe',
                            borderRadius: '0.375rem', fontSize: '0.85rem', color: '#1e40af'
                        }}>
                            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.85rem' }} />
                            Checking CRM for supplier "{extractedData.supplier_name}"...
                        </div>
                    ) : crmMatch.matched ? (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.6rem 1rem', backgroundColor: '#f0fdf4', border: '1px solid #86efac',
                            borderRadius: '0.375rem', fontSize: '0.85rem', color: '#166534'
                        }}>
                            <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.9rem' }} />
                            <span>
                                {crmMatch.addedFromCapture ? 'Added to CRM, details to complete: ' : 'Matched to CRM partner: '}
                                <strong>{crmMatch.partner.legal_entity_name}</strong>
                                {crmMatch.partner.trading_short_name && (
                                    <span style={{ color: '#4ade80' }}> ({crmMatch.partner.trading_short_name})</span>
                                )}
                                {crmMatch.addedFromCapture && (
                                    <span> · A reminder to finish it is in your bell.</span>
                                )}
                            </span>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '0.6rem 1rem', backgroundColor: '#fffbeb', border: '1px solid #fcd34d',
                            borderRadius: '0.375rem', fontSize: '0.85rem', color: '#92400e'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '0.9rem', color: '#f59e0b' }} />
                                <span>
                                    {crmMatch.lookupFailed
                                        ? `Could not check the CRM for supplier "${extractedData.supplier_name}": ${crmMatch.lookupFailed}`
                                        : `Supplier "${extractedData.supplier_name}" not found in CRM`}
                                </span>
                            </div>
                            {onAddPartner && !crmMatch.lookupFailed && (
                                <button
                                    type="button"
                                    onClick={onAddPartner}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '0.4rem',
                                        padding: '0.35rem 0.75rem', backgroundColor: '#f59e0b', color: 'white',
                                        border: 'none', borderRadius: '0.375rem', fontSize: '0.8rem',
                                        fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
                                    }}
                                >
                                    <FontAwesomeIcon icon={faUserPlus} /> Add Partner to CRM
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Header Fields */}
            <div className="extraction-section">
                <div className="section-header">
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
                        Invoice Header Information
                    </h4>
                    <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.75rem' }}>
                        Supplier details, dates, amounts, and references
                    </p>
                </div>
                <div className="extraction-fields-grid">
                    {headerFields.map(field => (
                        <FieldCard
                            key={field.key}
                            field={field}
                            value={extractedData[field.key]}
                            selected={selectedFields[field.key]}
                            onToggle={() => onFieldToggle(field.key)}
                        />
                    ))}
                </div>
            </div>

            {/* Defaulted Units Info */}
            {hasDefaultedUnits && unrecognizedUnits.length === 0 && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.6rem 1rem', marginBottom: '1rem',
                    backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe',
                    borderRadius: '0.375rem', fontSize: '0.85rem', color: '#1e40af'
                }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#3b82f6' }} />
                    No units were found on this invoice. Line items have been set to <strong style={{ margin: '0 0.25rem' }}>Custom</strong> -- you can change them using the dropdown in the Unit column below.
                </div>
            )}

            {/* Unrecognized Units Banner */}
            {unrecognizedUnits.length > 0 && (
                <div style={{
                    marginBottom: '1rem', border: '1px solid #fcd34d',
                    borderRadius: '0.375rem', overflow: 'hidden'
                }}>
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                        padding: '0.6rem 0.75rem', backgroundColor: '#fffbeb',
                        borderBottom: '1px solid #fde68a', fontSize: '0.85rem',
                        color: '#92400e', fontWeight: 600
                    }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: '#f59e0b' }} />
                        {unrecognizedUnits.length === 1
                            ? '1 unit from this invoice is not in your system'
                            : `${unrecognizedUnits.length} units from this invoice are not in your system`
                        }
                    </div>
                    {unrecognizedUnits.map(unitCode => (
                        <UnrecognizedUnitRow key={unitCode} unitCode={unitCode} />
                    ))}
                </div>
            )}

            {/* Line Items */}
            {lineItems.length > 0 && (
                <div className="extraction-section">
                    <div className="section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '1rem', fontWeight: 600 }}>
                                Line Items ({selectedLineItems ? selectedLineItems.filter(Boolean).length : lineItems.length}/{lineItems.length} selected)
                            </h4>
                            <p style={{ margin: '0 0 1rem 0', color: '#6b7280', fontSize: '0.75rem' }}>
                                Select which line items to include in the invoice
                            </p>
                        </div>
                        <div
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#3b82f6' }}
                            onClick={onToggleAllLineItems}
                        >
                            <FontAwesomeIcon icon={selectedLineItems && selectedLineItems.every(Boolean) ? faCheckSquare : faSquare} />
                            {selectedLineItems && selectedLineItems.every(Boolean) ? 'Deselect all' : 'Select all'}
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem',
                            border: '1px solid #e2e8f0', borderRadius: '0.375rem'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc' }}>
                                    <th style={{ ...thStyle, width: '30px', textAlign: 'center' }}></th>
                                    <th style={thStyle}>Description</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                                    <th style={thStyle}>Unit</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Unit Price</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>VAT %</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Net</th>
                                    <th style={{ ...thStyle, textAlign: 'right' }}>Gross</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.map((item, idx) => {
                                    const isSelected = selectedLineItems ? selectedLineItems[idx] : true;
                                    return (
                                        <tr
                                            key={idx}
                                            style={{
                                                borderBottom: '1px solid #f1f5f9',
                                                opacity: isSelected ? 1 : 0.45,
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? 'transparent' : '#f9fafb'
                                            }}
                                            onClick={() => onLineItemToggle(idx)}
                                        >
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <FontAwesomeIcon
                                                    icon={isSelected ? faCheckSquare : faSquare}
                                                    style={{ color: isSelected ? '#3b82f6' : '#9ca3af', fontSize: '0.9rem' }}
                                                />
                                            </td>
                                            <td style={tdStyle}>
                                                {item.item_description || '-'}
                                                {item.item_description_en && (
                                                    <div className="value-translation" style={{ color: '#64748b', fontStyle: 'italic' }}>
                                                        EN: {item.item_description_en}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.quantity || '-'}</td>
                                            <td style={tdStyle} onClick={(e) => {
                                                if (item._defaulted_unit || item._unrecognized_unit) e.stopPropagation();
                                            }}>
                                                {item._defaulted_unit ? (
                                                    <select
                                                        value={item.unit || 'CUSTOM'}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            onChangeDefaultedUnit(idx, e.target.value);
                                                        }}
                                                        style={{
                                                            padding: '0.15rem 0.3rem', fontSize: '0.75rem',
                                                            border: '1px solid #fcd34d', borderRadius: '0.25rem',
                                                            backgroundColor: '#fffbeb', color: '#92400e',
                                                            fontWeight: 500, cursor: 'pointer', maxWidth: '120px'
                                                        }}
                                                    >
                                                        <option value="CUSTOM">Custom</option>
                                                        {availableUnits.filter(u => u.code !== 'CUSTOM').map(u => (
                                                            <option key={u.id || u.code} value={u.code}>{u.code}</option>
                                                        ))}
                                                    </select>
                                                ) : item._unrecognized_unit ? (
                                                    <span style={{
                                                        padding: '0.1rem 0.35rem', backgroundColor: '#fef3c7',
                                                        border: '1px solid #fcd34d', borderRadius: '0.25rem',
                                                        fontSize: '0.75rem', color: '#92400e', fontWeight: 500
                                                    }}>
                                                        {item.unit || '-'}
                                                    </span>
                                                ) : (item.unit || '-')}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.unit_price || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.vat_rate_percentage ? `${item.vat_rate_percentage}%` : '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.net_amount || '-'}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.gross_amount || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Re-analysis */}
            <div className="form-group" style={{ marginTop: '2rem' }}>
                <label className="form-label" style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem', display: 'block' }}>
                    Need better results? Provide specific guidance for re-analysis:
                </label>
                <textarea
                    className="form-textarea"
                    placeholder="e.g., 'supplier name is ABC Corp', 'the invoice date is on page 2', 'VAT rate is 19%'"
                    value={reAnalysisGuidance}
                    onChange={(e) => setReAnalysisGuidance(e.target.value)}
                    disabled={isReAnalyzing}
                    rows={3}
                    style={{
                        width: '100%', padding: '0.75rem', border: '1px solid #e2e8f0',
                        borderRadius: '0.375rem', fontSize: '0.875rem', lineHeight: '1.5',
                        color: '#374151', backgroundColor: isReAnalyzing ? '#f9fafb' : 'white',
                        resize: 'vertical', minHeight: '80px', fontFamily: 'inherit',
                        outline: 'none', marginBottom: '1rem'
                    }}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={() => onReAnalyze(reAnalysisGuidance)}
                        disabled={isReAnalyzing || !reAnalysisGuidance.trim()}
                        className="btn btn-secondary"
                        type="button"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem',
                            padding: '0.75rem 1rem', backgroundColor: '#f8fafc', color: '#475569',
                            border: '1px solid #e2e8f0', borderRadius: '0.375rem', fontSize: '0.875rem',
                            fontWeight: 500, cursor: (isReAnalyzing || !reAnalysisGuidance.trim()) ? 'not-allowed' : 'pointer',
                            opacity: (isReAnalyzing || !reAnalysisGuidance.trim()) ? 0.6 : 1
                        }}
                    >
                        {isReAnalyzing ? (
                            <><FontAwesomeIcon icon={faSpinner} spin /> Re-analyzing...</>
                        ) : (
                            <><FontAwesomeIcon icon={faRobot} style={{ color: '#3b82f6' }} /> Re-analyze</>
                        )}
                    </button>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="modal-actions centered" style={{
                marginTop: '2rem', borderTop: '1px solid #f1f5f9',
                display: 'flex', gap: '0.75rem', justifyContent: 'center'
            }}>
                <button
                    onClick={onDiscard}
                    className="btn btn-secondary"
                    disabled={isReAnalyzing}
                    type="button"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '120px',
                        justifyContent: 'center', padding: '0.75rem 1rem', backgroundColor: '#ffffff',
                        color: '#475569', border: '1px solid #e2e8f0', borderRadius: '0.375rem',
                        fontSize: '0.875rem', fontWeight: 500,
                        cursor: isReAnalyzing ? 'not-allowed' : 'pointer',
                        opacity: isReAnalyzing ? 0.6 : 1
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.875rem' }} /> Discard
                </button>

                <button
                    onClick={onApply}
                    className="btn btn-primary"
                    disabled={isReAnalyzing}
                    type="button"
                    style={{
                        display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: '120px',
                        justifyContent: 'center', padding: '0.75rem 1rem',
                        background: !isReAnalyzing ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' : '#94a3b8',
                        color: 'white', border: 'none', borderRadius: '0.375rem',
                        fontSize: '0.875rem', fontWeight: 500,
                        cursor: isReAnalyzing ? 'not-allowed' : 'pointer',
                        opacity: isReAnalyzing ? 0.6 : 1,
                        boxShadow: !isReAnalyzing ? '0 4px 12px rgba(59, 130, 246, 0.4)' : 'none'
                    }}
                >
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.875rem' }} />
                    {hasSelectedFields
                        ? `Apply Selected (${Object.values(selectedFields).filter(Boolean).length})`
                        : 'Proceed without Data'
                    }
                </button>
            </div>
        </div>
    );
};

const thStyle = {
    padding: '0.5rem 0.75rem',
    textAlign: 'left',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '2px solid #e2e8f0',
    whiteSpace: 'nowrap',
};

const tdStyle = {
    padding: '0.5rem 0.75rem',
    color: '#4b5563',
};

export default InvoiceExtractionResults;
